import React, {useEffect, useMemo, useState} from 'react';
import {withTranslation} from 'react-i18next';
import {Dialog} from 'primereact/dialog';
import {Button} from 'primereact/button';
import {Dropdown} from 'primereact/dropdown';
import {InputNumber} from 'primereact/inputnumber';
import {ProgressBar} from 'primereact/progressbar';
import {Message} from 'primereact/message';
import axios from 'axios';

import {getZebraPrinters, sendZplToPrinter} from './ZebraBrowserPrintService';

const InventoryLabelPrintDialog = ({visible, onHide, materials = [], source = 'material', t}) => {
  const [copies, setCopies] = useState(1);
  const [printers, setPrinters] = useState([]);
  const [selectedPrinter, setSelectedPrinter] = useState(null);
  const [loadingPrinters, setLoadingPrinters] = useState(false);
  const [browserPrintError, setBrowserPrintError] = useState(null);
  const [preparedLabels, setPreparedLabels] = useState([]);
  const [printing, setPrinting] = useState(false);

  const uniqueMaterials = useMemo(
    () => Array.from(new Map(materials.filter(Boolean).map((item) => [item.id, item])).values()),
    [materials]
  );

  const apiCall = (url, data) => axios.post(
    process.env.REACT_APP_API_HOST + url,
    data,
    {
      headers: {
        Authorization: 'Bearer ' + localStorage.getItem('userToken'),
        'App-Network': localStorage.getItem('networkId')
      }
    }
  );

  useEffect(() => {
    if (!visible) return;

    setBrowserPrintError(null);
    setLoadingPrinters(true);
    getZebraPrinters()
      .then((devices) => {
        setPrinters(devices);
        setSelectedPrinter((current) => current || devices[0] || null);
        if (devices.length === 0) {
          setBrowserPrintError(t('noZebraPrinterAvailable'));
        }
      })
      .catch(() => {
        setPrinters([]);
        setSelectedPrinter(null);
        setBrowserPrintError(t('zebraBrowserPrintUnavailable'));
      })
      .finally(() => setLoadingPrinters(false));
  }, [visible, t]);

  useEffect(() => {
    if (!visible || uniqueMaterials.length === 0) {
      setPreparedLabels([]);
      return;
    }

    apiCall('/api/v2/inventory-label/prepare', {
      material_ids: uniqueMaterials.map((material) => material.id),
      copies
    })
      .then((response) => setPreparedLabels(response?.data?.data?.labels || []))
      .catch(() => setPreparedLabels([]));
  }, [visible, uniqueMaterials, copies]);

  const printLabels = async () => {
    if (!selectedPrinter || uniqueMaterials.length === 0) return;

    setPrinting(true);
    try {
      const materialIds = uniqueMaterials.map((material) => material.id);
      const response = await apiCall('/api/v2/inventory-label/prepare', {
        material_ids: materialIds,
        copies
      });
      const labels = response?.data?.data?.labels || [];

      for (const label of labels) {
        await sendZplToPrinter(selectedPrinter, label.zpl);
      }

      await apiCall('/api/v2/inventory-label/log', {
        material_ids: materialIds,
        copies,
        printer_name: selectedPrinter.name || selectedPrinter.uid || '',
        source
      });

      window.App.toastShow('success', t('successfulOperation'), t('labelsSentToPrinter'));
      onHide();
    } catch (error) {
      window.App.toastShow(
        'error',
        t('error'),
        error?.response?.data?.message || error.toString(),
        '',
        error.response
      );
    } finally {
      setPrinting(false);
    }
  };

  const footer = (
    <>
      <Button label={t('cancel')} className="p-button-secondary" onClick={onHide} disabled={printing} />
      <Button
        label={t('printLabels')}
        icon="pi pi-print"
        onClick={printLabels}
        disabled={printing || loadingPrinters || !selectedPrinter || uniqueMaterials.length === 0}
        loading={printing}
      />
    </>
  );

  return (
    <Dialog
      header={t('labelPrinting')}
      visible={visible}
      style={{width: '620px', maxWidth: '95vw'}}
      footer={footer}
      onHide={onHide}
    >
      {loadingPrinters && <ProgressBar mode="indeterminate" style={{height: '5px'}} />}
      {!loadingPrinters && browserPrintError && (
        <Message severity="warn" text={browserPrintError} className="p-mb-3" />
      )}

      <div className="p-grid p-mt-2">
        <div className="p-col-12 p-lg-8">
          <div className="p-field p-fluid">
            <label>{t('zebraPrinter')}</label>
            <Dropdown
              value={selectedPrinter}
              options={printers}
              optionLabel="name"
              placeholder={t('selectPrinter')}
              onChange={(event) => setSelectedPrinter(event.value)}
              disabled={loadingPrinters || printing}
            />
          </div>
        </div>
        <div className="p-col-12 p-lg-4">
          <div className="p-field p-fluid">
            <label>{t('numberOfCopies')}</label>
            <InputNumber
              value={copies}
              min={1}
              max={999}
              showButtons
              onValueChange={(event) => setCopies(event.value || 1)}
              disabled={printing}
            />
          </div>
        </div>
      </div>

      <div className="p-mt-3">
        <strong>{t('labelPreview')}</strong>
        {uniqueMaterials.map((material) => {
          const preparedLabel = preparedLabels.find(
            (label) => Number(label.material_id) === Number(material.id)
          );

          return (
            <div key={material.id} className="p-p-3 p-mt-2 p-text-center" style={{border: '1px solid var(--surface-d)', borderRadius: 6}}>
              <div className="p-mb-2"><strong>{material.name}</strong></div>
              {preparedLabel?.barcode_image && (
                <img
                  src={preparedLabel.barcode_image}
                  alt={`${material.name} ${t('barcode')}`}
                  style={{maxWidth: '100%', height: '60px', objectFit: 'contain'}}
                />
              )}
              <div>{preparedLabel?.barcode || material.upc || t('missingBarcode')}</div>
              <div>{t('sku')}: {material.sku || '-'}</div>
            </div>
          );
        })}
      </div>
    </Dialog>
  );
};

export default withTranslation('common')(InventoryLabelPrintDialog);
